import { memo, useCallback } from 'react'
import { MultiStrikeChartTypes, setMultiStrikeChartType } from '../../../reducer/multiStrikeChart.reducer'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi, selectMultiStrikeChartConfigShowChange } from '../../../selectors/chart'
import { selectMultiStrikeChartType, selectMultiStrikeCumOi, selectMultiStrikeShowChange } from '../../../selectors/multiStrikeChart'
import { useStyles } from './styles'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../../components/adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import {
    getMultiStrikeChartTypeMenuValue,
    isOiValueChartType,
    resolveMultiStrikeChartTypeSelection,
} from '../../../components/multiStikeChart/multistrikeChartTypeHelpers'

type Props = {
    viewKey: string
    isWidget: boolean
    compact?: boolean
}

const CHART_TYPE_MENU_LABELS = {
    price: 'Price',
    iv: 'IV',
    oi: 'OI',
    oiChange: 'OI Chg',
    straddle: 'Straddle',
    oiDiff: 'OI Diff',
} as const

type ChartTypeMenuValue = keyof typeof CHART_TYPE_MENU_LABELS

const getChartTypeDisplayName = (chartType: MultiStrikeChartTypes, cumOi: boolean, showChange: boolean): string => {
    return CHART_TYPE_MENU_LABELS[getMultiStrikeChartTypeMenuValue(chartType, cumOi, showChange)]
}

const MultiStrikeChartTypeMenu = ({ viewKey, isWidget, compact = false }: Props) => {
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const { user: { hasAccess } } = useUser()
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)

    const menuValue = getMultiStrikeChartTypeMenuValue(chartType, cumOi, showChange)

    const applyChartType = useCallback(
        (value: string) => {
            const nextConfig = resolveMultiStrikeChartTypeSelection(value as ChartTypeMenuValue, cumOi, showChange)
            const nextChartType = nextConfig.chartType

            if (isOiValueChartType(nextChartType) && !hasAccess(FeatureCategory.MULTI_STRIKE, FeatureSubCategory.OI_CHARTS)) {
                dispatch(setFeatureRestrictionDialog({
                    open: true,
                    feature: 'OI Charts',
                    featureRestrictionCode: 'MULTI_STRIKE_OI_CHARTS_MULTISTRIKE',
                    group: Group.MULTI_STRIKE
                }))
                return
            }

            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({
                    viewKey,
                    config: nextConfig,
                }))
            } else {
                dispatch(setMultiStrikeChartType(nextChartType))
            }
            AnalyticsManager.logEvent('Chart Type Changed', { type: nextChartType, source: 'multistrike' })
        },
        [dispatch, isWidget, viewKey, cumOi, showChange, hasAccess]
    )

    const handleChartTypeChange = useCallback(
        (_e: any, value: string) => {
            if (value) {
                applyChartType(value)
            }
        },
        [applyChartType]
    )

    if (compact) {
        return (
            <Typography
                component="span"
                sx={{ margin: 0, padding: '0 4px', fontSize: '13px', minWidth: 24, lineHeight: '24px', userSelect: 'none' }}
            >
                {getChartTypeDisplayName(chartType, cumOi, showChange)}
            </Typography>
        )
    }

    return (
        <ToggleButtonGroup
            id={'mulstrike-price-options'}
            className={classes.item}
            value={menuValue}
            exclusive
            size="small"
            onChange={handleChartTypeChange}
            aria-label="text alignment"
            sx={{
                textWrap: 'nowrap',
                height: '35px',
                '.MuiButtonBase-root': {
                    borderColor: (theme) => theme.palette.background.default
                },
                ml: isWidget ? '0 !important' : '8px !important'
            }}
        >
            <ToggleButton value="iv" aria-label="centered">
                Imp. Vol
            </ToggleButton>
            <ToggleButton value="oi" aria-label="right aligned">
                Open Int.
            </ToggleButton>
            <ToggleButton value="oiChange" aria-label="oi change">
                OI Chg
            </ToggleButton>
            <ToggleButton value="price" aria-label="left aligned">
                Price
            </ToggleButton>
            <ToggleButton value="straddle" aria-label="straddle">
                Straddle
            </ToggleButton>
            <ToggleButton value="oiDiff" aria-label="oi diff">
                OI Diff
            </ToggleButton>
        </ToggleButtonGroup>
    )
}

export default memo(MultiStrikeChartTypeMenu)
